import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

// Track single event
export const trackEvent = async (eventData) => {
  try {
    await fetch(`${backendUrl}/api/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(eventData),
    });
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

// Track product view with duration
export const trackProductView = async (productId, duration, scrollDepth) => {
  try {
    await fetch(`${backendUrl}/api/analytics/product-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ product_id: productId, duration, scroll_depth: scrollDepth }),
    });
  } catch (error) {
    console.error('Product view tracking error:', error);
  }
};

// Hook for page view tracking
export const usePageTracking = () => {
  const location = useLocation();
  const pageStartTime = useRef(Date.now());

  useEffect(() => {
    // Track page view on mount
    trackEvent({
      event_type: 'page_view',
      page: location.pathname,
    });

    // Reset timer
    pageStartTime.current = Date.now();

    // Track time spent on unmount
    return () => {
      const duration = Date.now() - pageStartTime.current;
      if (duration > 1000) { // Only track if more than 1 second
        trackEvent({
          event_type: 'page_leave',
          page: location.pathname,
          duration,
        });
      }
    };
  }, [location.pathname]);
};

// Hook for product detail tracking
export const useProductTracking = (productId) => {
  const startTime = useRef(Date.now());
  const maxScrollDepth = useRef(0);

  useEffect(() => {
    if (!productId) return;

    startTime.current = Date.now();
    maxScrollDepth.current = 0;

    // Track scroll depth
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);

    // Track product view event
    trackEvent({
      event_type: 'product_view',
      product_id: productId,
      page: `/urun/${productId}`,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      
      // Track product view duration on unmount
      const duration = Date.now() - startTime.current;
      if (duration > 1000) {
        trackProductView(productId, duration, maxScrollDepth.current);
      }
    };
  }, [productId]);

  const trackAddToCart = useCallback(() => {
    trackEvent({
      event_type: 'add_to_cart',
      product_id: productId,
    });
  }, [productId]);

  const trackAddToFavorites = useCallback(() => {
    trackEvent({
      event_type: 'add_to_favorites',
      product_id: productId,
    });
  }, [productId]);

  return { trackAddToCart, trackAddToFavorites };
};

// Hook for search tracking
export const useSearchTracking = () => {
  const trackSearch = useCallback((query) => {
    trackEvent({
      event_type: 'search',
      search_query: query,
    });
  }, []);

  return { trackSearch };
};

// Hook for filter tracking  
export const useFilterTracking = () => {
  const trackFilter = useCallback((filterType, filterValue) => {
    trackEvent({
      event_type: 'filter',
      filter_type: filterType,
      filter_value: filterValue,
    });
  }, []);

  return { trackFilter };
};

export default {
  trackEvent,
  trackProductView,
  usePageTracking,
  useProductTracking,
  useSearchTracking,
  useFilterTracking,
};
